// components/ui/HaitiFlagAnimation.js
import React from 'react';

const HaitiFlagAnimation = ({ className = "w-24 h-16" }) => {
 return (
 <div className={`${className} relative overflow-hidden rounded-lg shadow-lg`}>
 {/* Flag Container */}
 <div className="relative w-full h-full">
 {/* Blue Stripe (Top) */}
 <div className="absolute top-0 left-0 w-full h-1/2 bg-blue-600 flag-wave-animation" 
 style={{ 
 background: 'linear-gradient(45deg, #1e40af 0%, #3b82f6 25%, #1e40af 50%, #3b82f6 75%, #1e40af 100%)',
 backgroundSize: '20px 20px',
 animation: 'flagWave 3s ease-in-out infinite, blueFlagShimmer 4s ease-in-out infinite'
 }}>
 </div>
 
 {/* Red Stripe (Bottom) */}
 <div className="absolute bottom-0 left-0 w-full h-1/2 bg-red-600 flag-wave-animation"
 style={{ 
 background: 'linear-gradient(45deg, #dc2626 0%, #ef4444 25%, #dc2626 50%, #ef4444 75%, #dc2626 100%)',
 backgroundSize: '20px 20px',
 animation: 'flagWave 3s ease-in-out infinite 0.5s, redFlagShimmer 4s ease-in-out infinite 0.5s'
 }}>
 </div>
 
 {/* Center Coat of Arms (simplified) */}
 <div className="absolute inset-0 flex items-center justify-center">
 <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
 style={{ animation: 'coatOfArmsFloat 2s ease-in-out infinite' }}>
 <div className="w-6 h-6 bg-gradient-to-br from-blue-600 to-red-600 rounded-full flex items-center justify-center">
 <span className="text-xs text-white font-bold"></span>
 </div>
 </div>
 </div>
 
 {/* Wave Effect Overlay */}
 <div className="absolute inset-0 opacity-30"
 style={{
 background: 'linear-gradient(45deg, transparent 40%, rgba(255,255,255,0.1) 50%, transparent 60%)',
 animation: 'flagShine 3s ease-in-out infinite'
 }}>
 </div>
 
 {/* Wind Effect */}
 <div className="absolute top-0 right-0 w-2 h-full bg-gradient-to-l from-black/10 to-transparent"
 style={{ animation: 'windEffect 2s ease-in-out infinite alternate' }}>
 </div>
 </div>
 
 {/* CSS Animations */}
 <style jsx>{`
 @keyframes flagWave {
 0%, 100% {
 transform: translateX(0px) skewX(0deg);
 }
 25% {
 transform: translateX(2px) skewX(1deg);
 }
 50% {
 transform: translateX(-1px) skewX(-0.5deg);
 }
 75% {
 transform: translateX(1px) skewX(0.5deg);
 }
 }
 
 @keyframes blueFlagShimmer {
 0%, 100% {
 background-position: 0% 0%;
 }
 50% {
 background-position: 100% 100%;
 }
 }
 
 @keyframes redFlagShimmer {
 0%, 100% {
 background-position: 100% 100%;
 }
 50% {
 background-position: 0% 0%;
 }
 }
 
 @keyframes coatOfArmsFloat {
 0%, 100% {
 transform: translateY(0px) scale(1);
 }
 50% {
 transform: translateY(-2px) scale(1.05);
 }
 }
 
 @keyframes flagShine {
 0% {
 transform: translateX(-100%) skewX(-25deg);
 }
 50% {
 transform: translateX(0%) skewX(-25deg);
 }
 100% {
 transform: translateX(100%) skewX(-25deg);
 }
 }
 
 @keyframes windEffect {
 0% {
 transform: scaleX(1);
 opacity: 0.1;
 }
 100% {
 transform: scaleX(1.5);
 opacity: 0.3;
 }
 }
 
 .flag-wave-animation {
 transform-origin: left center;
 }
 `}</style>
 </div>
 );
};

// Enhanced version with more realistic flag physics 
export const RealisticHaitiFlagAnimation = ({ className = "w-32 h-20" }) => {
 return (
 <div className={`${className} relative overflow-hidden rounded-lg shadow-2xl`}>
 {/* Flag Container with 3D perspective */}
 <div className="relative w-full h-full" style={{ perspective: '400px' }}>
 {/* Blue Stripe (Top) with wave simulation */}
 <div className="absolute top-0 left-0 w-full h-1/2 overflow-hidden">
 <div 
 className="w-full h-full"
 style={{ 
 background: `
 radial-gradient(ellipse at 20% 30%, #3b82f6 0%, #1e40af 100%),
 linear-gradient(45deg, #1e40af 0%, #3b82f6 25%, #1e40af 50%, #3b82f6 75%, #1e40af 100%)
 `,
 backgroundSize: '200% 200%, 40px 40px',
 animation: 'realisticWaveBlue 4s ease-in-out infinite, flagTexture 6s ease-in-out infinite'
 }}>
 </div>
 </div>
 
 {/* Red Stripe (Bottom) with wave simulation */}
 <div className="absolute bottom-0 left-0 w-full h-1/2 overflow-hidden">
 <div 
 className="w-full h-full"
 style={{ 
 background: `
 radial-gradient(ellipse at 80% 70%, #ef4444 0%, #dc2626 100%),
 linear-gradient(45deg, #dc2626 0%, #ef4444 25%, #dc2626 50%, #ef4444 75%, #dc2626 100%)
 `,
 backgroundSize: '200% 200%, 40px 40px',
 animation: 'realisticWaveRed 4s ease-in-out infinite 1s, flagTexture 6s ease-in-out infinite 1s'
 }}>
 </div>
 </div>
 
 {/* Enhanced Coat of Arms */}
 <div className="absolute inset-0 flex items-center justify-center">
 <div className="relative w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl border-2 border-gray-200"
 style={{ animation: 'coatFloat 3s ease-in-out infinite, coatGlow 4s ease-in-out infinite' }}>
 <div className="w-8 h-8 bg-gradient-to-br from-blue-600 via-blue-700 to-red-600 rounded-full flex items-center justify-center shadow-inner">
 <div className="text-sm" style={{ animation: 'symbolRotate 8s linear infinite' }}>
 
 </div>
 </div>
 {/* Coat of arms glow effect */}
 <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-400/20 to-red-400/20"
 style={{ animation: 'coatAura 3s ease-in-out infinite alternate' }}>
 </div>
 </div>
 </div>
 
 {/* Realistic wind and lighting effects */}
 <div className="absolute inset-0">
 {/* Primary wind wave */}
 <div className="absolute inset-0 opacity-20"
 style={{
 background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 30%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0.3) 70%, transparent 100%)',
 animation: 'windSweep 5s ease-in-out infinite'
 }}>
 </div>
 
 {/* Secondary ripple effect */}
 <div className="absolute inset-0 opacity-10"
 style={{
 background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)',
 animation: 'rippleEffect 3s ease-in-out infinite'
 }}>
 </div>
 
 {/* Flag pole shadow simulation */}
 <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-r from-black/30 to-transparent"
 style={{ animation: 'poleEffect 4s ease-in-out infinite' }}>
 </div>
 </div>
 </div>
 
 {/* Enhanced CSS Animations */}
 <style jsx>{`
 @keyframes realisticWaveBlue {
 0%, 100% {
 transform: translateX(0px) rotateY(0deg) scaleX(1);
 background-position: 0% 0%, 0% 0%;
 }
 25% {
 transform: translateX(3px) rotateY(2deg) scaleX(1.02);
 background-position: 25% 25%, 25% 25%;
 }
 50% {
 transform: translateX(-2px) rotateY(-1deg) scaleX(0.98);
 background-position: 50% 50%, 50% 50%;
 }
 75% {
 transform: translateX(2px) rotateY(1deg) scaleX(1.01);
 background-position: 75% 75%, 75% 75%;
 }
 }
 
 @keyframes realisticWaveRed {
 0%, 100% {
 transform: translateX(0px) rotateY(0deg) scaleX(1);
 background-position: 100% 100%, 100% 100%;
 }
 25% {
 transform: translateX(-2px) rotateY(-1deg) scaleX(0.98);
 background-position: 75% 75%, 75% 75%;
 }
 50% {
 transform: translateX(3px) rotateY(2deg) scaleX(1.02);
 background-position: 50% 50%, 50% 50%;
 }
 75% {
 transform: translateX(-1px) rotateY(-0.5deg) scaleX(1.01);
 background-position: 25% 25%, 25% 25%;
 }
 }
 
 @keyframes flagTexture {
 0%, 100% {
 filter: brightness(1) contrast(1);
 }
 50% {
 filter: brightness(1.1) contrast(1.05);
 }
 }
 
 @keyframes coatFloat {
 0%, 100% {
 transform: translateY(0px) scale(1) rotate(0deg);
 }
 33% {
 transform: translateY(-1px) scale(1.02) rotate(1deg);
 }
 66% {
 transform: translateY(1px) scale(0.98) rotate(-1deg);
 }
 }
 
 @keyframes coatGlow {
 0%, 100% {
 box-shadow: 0 0 10px rgba(59, 130, 246, 0.3), 0 0 20px rgba(239, 68, 68, 0.2);
 }
 50% {
 box-shadow: 0 0 15px rgba(239, 68, 68, 0.4), 0 0 25px rgba(59, 130, 246, 0.3);
 }
 }
 
 @keyframes symbolRotate {
 0% { transform: rotate(0deg); }
 100% { transform: rotate(360deg); }
 }
 
 @keyframes coatAura {
 0% {
 transform: scale(1);
 opacity: 0.3;
 }
 100% {
 transform: scale(1.1);
 opacity: 0.1;
 }
 }
 
 @keyframes windSweep {
 0% {
 transform: translateX(-100%) skewX(-15deg);
 }
 30% {
 transform: translateX(-50%) skewX(-10deg);
 }
 70% {
 transform: translateX(50%) skewX(-10deg);
 }
 100% {
 transform: translateX(100%) skewX(-15deg);
 }
 }
 
 @keyframes rippleEffect {
 0%, 100% {
 transform: translateY(0px);
 opacity: 0.1;
 }
 50% {
 transform: translateY(-2px);
 opacity: 0.2;
 }
 }
 
 @keyframes poleEffect {
 0%, 100% {
 transform: scaleY(1) scaleX(1);
 }
 50% {
 transform: scaleY(1.05) scaleX(1.2);
 }
 }
 `}</style>
 </div>
 );
};

export default HaitiFlagAnimation;